// ============================================================================
// FilePreviewModal — in-app lightbox for stored files
// ============================================================================
// Self-contained preview dialog that fetches the file as a Blob through
// authenticated axios (so headers travel) and renders an <img> or PDF
// <iframe> over a backdrop.
//
// Why we don't just open the API URL in a new tab:
//   - Raw <a target="_blank"> / <img src="…/Files/download?path=…"> don't
//     carry the Authorization or X-Tenant-ID headers. The API rejects with
//     401 / MISSING_TENANT_ID.
//   - This component fetches the bytes via axios, converts to a blob: URL,
//     and feeds that to <img>/<iframe> — no auth needed for the local URL.
//
// Lifecycle:
//   - Mounts → starts loading → shows spinner.
//   - Success → renders preview.
//   - On unmount or close → revokes the blob URL.
//   - Backdrop is non-dismissive (matches confirmation-dialog convention).
//     Closes only via Esc or the Close button.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { axiosClient } from 'api/client/axiosClient';
import { fileStorageEndpoints } from 'api/endpoints/fileStorage.endpoints';

/**
 * Heuristic to map a file extension → MIME type. We control the extension at
 * upload time (derived from the validated content-type), so this is reliable
 * when the caller only has the relative path and no metadata. Falls back to
 * an empty string when unknown — the modal then leans on the actual blob's
 * MIME (from the response headers) before final render.
 */
const inferContentTypeFromName = (nameOrPath: string): string => {
  const ext = (nameOrPath.split('.').pop() || '').toLowerCase();
  switch (ext) {
    case 'pdf':  return 'application/pdf';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'png':  return 'image/png';
    case 'webp': return 'image/webp';
    case 'heic': return 'image/heic';
    default:     return '';
  }
};

export interface FilePreviewModalProps {
  /** Adapter-relative path returned by the upload endpoint. */
  relativePath: string;
  /** Display name shown in the header. Falls back to the basename of `relativePath`. */
  fileName?: string;
  /** MIME type — drives the renderer (`application/pdf` → iframe, otherwise img). */
  contentType?: string;
  /** Optional human-friendly size string ("97.6 KB") shown in the header. */
  sizeLabel?: string;
  /** Fired when the user closes the modal. */
  onClose: () => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  relativePath,
  fileName,
  contentType,
  sizeLabel,
  onClose,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  /** MIME picked up from the actual response headers — wins over the prop / extension hint. */
  const [responseMime, setResponseMime] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch the file as a blob and create a local URL. We read the response
  // Content-Type ourselves (instead of using fileStorageService.openAsObjectUrl)
  // so we can branch on the actual MIME for the renderer (img vs iframe) —
  // callers that only have the relative path don't always know the type.
  // Cleanup revokes the URL and cancels handling if the modal unmounts mid-flight.
  useEffect(() => {
    let cancelled = false;
    let urlToRevoke: string | null = null;
    setLoading(true);
    setError(null);
    axiosClient
      .get(fileStorageEndpoints.downloadUrl(relativePath), { responseType: 'blob' })
      .then((resp) => {
        if (cancelled) return;
        const mime = resp.data?.type || resp.headers?.['content-type'] || '';
        const url = URL.createObjectURL(resp.data);
        urlToRevoke = url;
        setBlobUrl(url);
        setResponseMime(mime);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Unable to load file.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [relativePath]);

  // Close on Escape so the lightbox matches the rest of the app's modal contract.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const displayName = fileName || relativePath.split('/').pop() || 'file';
  // Effective MIME — response header (truth) wins; otherwise caller-supplied
  // prop; otherwise inferred from the file extension. Memoised so the choice
  // is stable across renders.
  const effectiveMime = useMemo(
    () => responseMime || contentType || inferContentTypeFromName(fileName || relativePath),
    [responseMime, contentType, fileName, relativePath],
  );
  const isPdf = effectiveMime === 'application/pdf';
  const isImage = effectiveMime.startsWith('image/');
  const headerMeta = [effectiveMime, sizeLabel].filter(Boolean).join(' · ');

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 11000,
        padding: '2rem',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${displayName}`}
    >
      {/* Backdrop is intentionally non-dismissive — close only via the Close
          button or Esc. Matches the destructive-dialog convention. */}
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '8px',
          boxShadow: 'var(--shadow-xl)',
          // Width: never narrower than what's needed for the header bar, never
          // wider than the viewport. PDFs ask for 80vw; images cap at 90vw.
          minWidth: 'min(480px, 90vw)',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.625rem 1rem',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--modal-header-bg)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div
              style={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={displayName}
            >
              {displayName}
            </div>
            {headerMeta && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{headerMeta}</div>
            )}
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
            title="Close preview (Esc)"
            autoFocus
          >
            <IconSystem.x size={16} /> Close
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-secondary)',
            minHeight: 0,
            minWidth: '320px',
          }}
        >
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '2rem' }}>
              <div
                style={{
                  width: '2rem',
                  height: '2rem',
                  border: '3px solid var(--border-color)',
                  borderTopColor: '#2563eb',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Loading…</span>
            </div>
          )}
          {!loading && error && (
            <div style={{ padding: '2rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              {error}
            </div>
          )}
          {!loading && !error && blobUrl && (
            isPdf ? (
              <iframe
                src={blobUrl}
                title={displayName}
                style={{ width: '80vw', height: '80vh', border: 0, background: 'white' }}
              />
            ) : isImage ? (
              <img
                src={blobUrl}
                alt={displayName}
                style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
              />
            ) : (
              <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                <IconSystem.file size={32} />
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Inline preview isn't supported for this file type.
                </div>
                <a
                  href={blobUrl}
                  download={displayName}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.8125rem' }}
                >
                  Download to view
                </a>
              </div>
            )
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FilePreviewModal;
